import { Alert, Box, Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { FoodsIcon } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { CONSENT_CATEGORY_CONFIG, CONSENT_TYPE_ICON_CONFIG } from "./consentCategoryConfig";
import { ConsentRichTextField } from "./ConsentRichTextField";
import {
    type ConsentCreationState,
    type ConsentCreationType,
    type ConsentDraftLabel,
    type ConsentDraftVersion,
    buildGeneratedTitle,
    getAvailableCategoryKeys,
    getCategoryDescription,
    getContentLabel,
    getContentPlaceholder,
    getLabelsInfo,
    getTypeHint,
    getTypeLabel,
    getVersionInfo,
} from "./consentCreationShared";
import { type Locale } from "src/api/locales/useLocales";

type CommonStepProps = {
    activeLocale: string;
    localeOptions: Locale[];
    setActiveLocale: (value: string) => void;
};

type TypeCategoryStepProps = {
    errors: { category: string; code: string };
    state: ConsentCreationState;
    onCategoryChange: (value: string) => void;
    onCodeChange: (value: string) => void;
    onTypeChange: (value: ConsentCreationType) => void;
};

type LabelsStepProps = CommonStepProps & {
    currentLabelDraft: ConsentDraftLabel;
    errors: { description: string; name: string };
    isCurrentLocaleDefault: boolean;
    state: ConsentCreationState;
    onDescriptionChange: (value: string) => void;
    onNameChange: (value: string) => void;
};

type VersionStepProps = CommonStepProps & {
    currentLabelDraft: ConsentDraftLabel;
    currentVersionDraft: ConsentDraftVersion;
    errors: { content: string; documentUrl: string; version: string };
    isCurrentLocaleDefault: boolean;
    publicationPreview: string;
    state: ConsentCreationState;
    onContentChange: (value: string) => void;
    onDocumentUrlChange: (value: string) => void;
    onVersionChange: (value: string) => void;
    onVersionNoteChange: (value: string) => void;
};

const useFieldBackgroundSx = () => {
    const theme = useTheme();

    return {
        "& .MuiOutlinedInput-root": {
            bgcolor: theme.palette.common.white,
            borderRadius: theme.spacing(2),
            "& fieldset": {
                borderColor: theme.custom.grey[500],
            },
            "&:hover fieldset": {
                borderColor: theme.custom.grey[1000],
            },
            "&.Mui-focused fieldset": {
                borderColor: theme.palette.primary.main,
                borderWidth: 1,
            },
        },
        "& .MuiFormLabel-root.Mui-focused": {
            color: theme.palette.primary.main,
        },
        "& .MuiFormHelperText-root": {
            marginLeft: 0,
        },
    };
};

const SelectionIndicator = ({ selected }: { selected: boolean }) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                border: `2px solid ${selected ? theme.palette.primary.main : theme.custom.grey[500]}`,
                bgcolor: theme.palette.common.white,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
            }}
        >
            <Box
                sx={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    bgcolor: selected ? theme.palette.primary.main : "transparent",
                    transition: "all 160ms ease",
                }}
            />
        </Box>
    );
};

export const ConsentTypeCategoryStep = ({
    errors,
    state,
    onCategoryChange,
    onCodeChange,
    onTypeChange,
}: TypeCategoryStepProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const fieldBackgroundSx = useFieldBackgroundSx();
    const availableCategories = getAvailableCategoryKeys(state.type);
    const selectedColor = theme.palette.primary.main;

    return (
        <Stack gap={4}>
            <Box>
                <Typography variant="body" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {t("settings.consent.document_wizard.type_title", "Purpose type")}
                </Typography>
                <Box sx={{ mb: 2 }} />
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                    {(["opt_in", "document"] as const).map((type) => {
                        const isSelected = state.type === type;
                        const icon = CONSENT_TYPE_ICON_CONFIG[type].icon;

                        return (
                            <Box
                                key={type}
                                onClick={() => onTypeChange(type)}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: `2px solid ${isSelected ? selectedColor : theme.custom.grey[500]}`,
                                    bgcolor: isSelected ? alpha(selectedColor, 0.06) : theme.palette.common.white,
                                    cursor: "pointer",
                                    transition: "all 160ms ease",
                                }}
                            >
                                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
                                    <Stack gap={1.25}>
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                bgcolor: alpha(selectedColor, 0.12),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: selectedColor,
                                            }}
                                        >
                                            <FoodsIcon icon={icon} size={2} />
                                        </Box>
                                        <Typography variant="body" sx={{ fontWeight: 700 }}>
                                            {getTypeLabel(type, t)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                            {getTypeHint(type, t)}
                                        </Typography>
                                    </Stack>
                                    <SelectionIndicator selected={isSelected} />
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box>
                <Typography variant="body" sx={{ fontWeight: 700, mb: 0.75 }}>
                    {t("settings.consent.document_wizard.category_title", "Category")}
                </Typography>
                <Typography variant="body2" sx={{ color: theme.custom.grey[2000], mb: 2 }}>
                    {t(
                        "settings.consent.document_wizard.category_hint",
                        "Determines how the purpose is presented to the user.",
                    )}
                </Typography>
                <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
                    {availableCategories.map((categoryKey) => {
                        const categoryConfig = CONSENT_CATEGORY_CONFIG[categoryKey];
                        const isSelected = state.category === categoryKey;
                        const color =
                            categoryConfig.colorKey === "grey" ?
                                theme.custom.grey[2000]
                            :   theme.palette[categoryConfig.colorKey].main;

                        return (
                            <Box
                                key={categoryKey}
                                onClick={() => onCategoryChange(categoryKey)}
                                sx={{
                                    p: 3,
                                    borderRadius: 3,
                                    border: `2px solid ${isSelected ? color : theme.custom.grey[500]}`,
                                    bgcolor: isSelected ? alpha(color, 0.06) : theme.palette.common.white,
                                    cursor: "pointer",
                                    transition: "all 160ms ease",
                                }}
                            >
                                <Stack direction="row" gap={2} alignItems="flex-start" justifyContent="space-between">
                                    <Stack direction="row" gap={2} alignItems="flex-start" sx={{ minWidth: 0 }}>
                                        <Box
                                            sx={{
                                                width: 44,
                                                height: 44,
                                                borderRadius: 2,
                                                bgcolor: alpha(color, 0.1),
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                flexShrink: 0,
                                                color,
                                            }}
                                        >
                                            <FoodsIcon icon={categoryConfig.icon} size={2} />
                                        </Box>
                                        <Stack gap={0.75} sx={{ minWidth: 0 }}>
                                            <Typography
                                                variant="body"
                                                sx={{
                                                    fontWeight: 700,
                                                    color: isSelected ? color : theme.palette.text.primary,
                                                }}
                                            >
                                                {t(categoryConfig.labelKey, categoryConfig.labelFallback)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: theme.custom.grey[2000] }}>
                                                {getCategoryDescription(categoryKey, t)}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <SelectionIndicator selected={isSelected} />
                                </Stack>
                            </Box>
                        );
                    })}
                </Box>
                {errors.category && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.75, display: "block" }}>
                        {errors.category}
                    </Typography>
                )}
            </Box>

            <TextField
                label={t("settings.consent.document_wizard.code_label", "Technical key")}
                placeholder={t("settings.consent.document_wizard.code_placeholder", "e.g. platform_policy")}
                value={state.code}
                onChange={(event) => onCodeChange(event.target.value)}
                error={Boolean(errors.code)}
                helperText={
                    errors.code ||
                    t(
                        "settings.consent.document_wizard.code_helper",
                        "Lowercase letters, numbers and underscores only.",
                    )
                }
                fullWidth
                sx={fieldBackgroundSx}
            />
        </Stack>
    );
};

export const ConsentLabelsStep = ({
    activeLocale,
    currentLabelDraft,
    errors,
    isCurrentLocaleDefault,
    localeOptions,
    setActiveLocale,
    state,
    onDescriptionChange,
    onNameChange,
}: LabelsStepProps) => {
    const { t } = useTranslation();
    const fieldBackgroundSx = useFieldBackgroundSx();
    const theme = useTheme();

    return (
        <Stack gap={3}>
            <Alert
                icon={<InfoIcon fontSize="inherit" />}
                severity="info"
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                }}
            >
                {getLabelsInfo(state.type, t)}
            </Alert>
            <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems={{ md: "center" }}>
                <TextField
                    select
                    label={t("settings.consent.document_wizard.locale_label", "Language")}
                    value={activeLocale}
                    onChange={(event) => setActiveLocale(event.target.value)}
                    sx={{ minWidth: 260, ...fieldBackgroundSx }}
                >
                    {localeOptions.map((locale) => (
                        <MenuItem key={locale.code} value={locale.code ?? ""}>
                            {`${locale.nativeName || locale.name || locale.code} (${locale.code})`}
                        </MenuItem>
                    ))}
                </TextField>
                <Chip
                    label={
                        isCurrentLocaleDefault ?
                            t("settings.consent.document_wizard.locale_required", "Required")
                        :   t("settings.consent.document_wizard.locale_optional", "Optional")
                    }
                    variant="outlined"
                    sx={{
                        alignSelf: { xs: "flex-start", md: "center" },
                        borderRadius: "999px",
                        borderColor:
                            isCurrentLocaleDefault ? alpha(theme.palette.primary.main, 0.24) : theme.custom.grey[500],
                        bgcolor:
                            isCurrentLocaleDefault ?
                                alpha(theme.palette.primary.main, 0.08)
                            :   theme.palette.common.white,
                        color: isCurrentLocaleDefault ? theme.palette.primary.main : theme.custom.grey[2000],
                        fontWeight: 600,
                    }}
                />
            </Stack>
            <TextField
                label={t("settings.consent.document_wizard.name_label", "Label")}
                placeholder={t("settings.consent.document_wizard.name_placeholder", "e.g. Platform usage policy")}
                value={currentLabelDraft.name}
                onChange={(event) => onNameChange(event.target.value)}
                error={Boolean(isCurrentLocaleDefault && errors.name)}
                helperText={isCurrentLocaleDefault ? errors.name : ""}
                fullWidth
                sx={fieldBackgroundSx}
            />
            <ConsentRichTextField
                editorKey={`consent-description-${activeLocale}`}
                id={`consent-description-${activeLocale}`}
                label={t("settings.consent.document_wizard.description_label", "Internal description")}
                placeholder={t(
                    "settings.consent.document_wizard.description_placeholder",
                    "Internal admin notes. Not displayed to users.",
                )}
                value={currentLabelDraft.description}
                error={Boolean(isCurrentLocaleDefault && errors.description)}
                helperText={isCurrentLocaleDefault ? errors.description : ""}
                minHeight={140}
                onChange={onDescriptionChange}
            />
        </Stack>
    );
};

export const ConsentVersionStep = ({
    activeLocale,
    currentLabelDraft,
    currentVersionDraft,
    errors,
    isCurrentLocaleDefault,
    localeOptions,
    publicationPreview,
    setActiveLocale,
    state,
    onContentChange,
    onDocumentUrlChange,
    onVersionChange,
    onVersionNoteChange,
}: VersionStepProps) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const fieldBackgroundSx = useFieldBackgroundSx();
    const accentColor = theme.palette.primary.main;

    return (
        <Stack gap={3}>
            <Alert
                icon={<InfoIcon fontSize="inherit" />}
                severity="info"
                sx={{
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
                    bgcolor: alpha(theme.palette.info.main, 0.08),
                }}
            >
                {getVersionInfo(state.type, t)}
            </Alert>
            <Stack direction={{ xs: "column", md: "row" }} gap={2}>
                <TextField
                    label={t("settings.consent.document_wizard.version_label", "Version number")}
                    value={state.version}
                    onChange={(event) => onVersionChange(event.target.value)}
                    error={Boolean(errors.version)}
                    helperText={errors.version}
                    sx={{ flex: 1, ...fieldBackgroundSx }}
                />
                <TextField
                    label={t("settings.consent.document_wizard.publish_date_label", "Publication date")}
                    value={publicationPreview}
                    InputProps={{ readOnly: true }}
                    helperText={t("settings.consent.document_wizard.publish_date_helper", "Automatic on creation")}
                    sx={{ flex: 1, ...fieldBackgroundSx }}
                />
            </Stack>
            <Stack direction={{ xs: "column", md: "row" }} gap={2} alignItems={{ md: "center" }}>
                <TextField
                    select
                    label={t("settings.consent.document_wizard.locale_label", "Language")}
                    value={activeLocale}
                    onChange={(event) => setActiveLocale(event.target.value)}
                    sx={{ minWidth: 260, ...fieldBackgroundSx }}
                >
                    {localeOptions.map((locale) => (
                        <MenuItem key={locale.code} value={locale.code ?? ""}>
                            {`${locale.nativeName || locale.name || locale.code} (${locale.code})`}
                        </MenuItem>
                    ))}
                </TextField>
                <Chip
                    label={buildGeneratedTitle(
                        currentLabelDraft.name || t("settings.consent.document_wizard.untitled", "Untitled"),
                    )}
                    variant="outlined"
                    sx={{
                        borderRadius: "999px",
                        borderColor: alpha(accentColor, 0.24),
                        bgcolor: alpha(accentColor, 0.05),
                        color: accentColor,
                        fontWeight: 600,
                    }}
                />
            </Stack>
            <ConsentRichTextField
                editorKey={`consent-content-${activeLocale}`}
                id={`consent-content-${activeLocale}`}
                label={getContentLabel(state.type, t)}
                placeholder={getContentPlaceholder(state.type, t)}
                value={currentVersionDraft.content}
                error={Boolean(isCurrentLocaleDefault && errors.content)}
                helperText={isCurrentLocaleDefault ? errors.content : ""}
                minHeight={260}
                onChange={onContentChange}
            />
            <TextField
                label={t("settings.consent.document_wizard.document_url_label", "Document URL (optional)")}
                placeholder="https://..."
                value={currentVersionDraft.documentUrl}
                onChange={(event) => onDocumentUrlChange(event.target.value)}
                error={Boolean(isCurrentLocaleDefault && errors.documentUrl)}
                helperText={
                    isCurrentLocaleDefault ?
                        errors.documentUrl ||
                        t(
                            "settings.consent.document_wizard.document_url_helper",
                            "Use this when the full document is hosted externally.",
                        )
                    :   ""
                }
                fullWidth
                sx={fieldBackgroundSx}
            />
            <TextField
                label={t("settings.consent.document_wizard.version_note_label", "Version note")}
                value={state.versionNote}
                onChange={(event) => onVersionNoteChange(event.target.value)}
                fullWidth
                sx={fieldBackgroundSx}
            />
        </Stack>
    );
};
